// ---------------------------------------------------------------------------
// Sync Status Store — Phase 4: Unified sync status tracking
// Shared store for real-time sync status across system-web, tenant-web, mobile-pwa
//
// TODO (audit Priority 2 — sync architecture audit, 2026-05-12):
// - Add a full @fileoverview JSDoc block per the project code-documentation
//   rule (Responsibilities, Dependencies, Architecture, Notes). Add @contract
//   blocks for SyncStatus, SyncErrorState, SyncProgress and @localFirst
//   documenting that this store is Dexie-derived state — UI components read
//   from this store, never from sync APIs directly.
// ---------------------------------------------------------------------------

import { subscribeLinkQuality, type LinkQuality } from '../services/connectionQuality';

export type SyncErrorType = 'network' | 'auth' | 'conflict' | 'server' | 'tenant-not-found' | 'unknown';

export interface SyncErrorState {
  message: string;
  timestamp: string;
  type: SyncErrorType;
}

export type SyncTransport = 'websocket' | 'sse' | 'polling' | 'unknown';
export type ConnectionState = 'connected' | 'connecting' | 'reconnecting' | 'disconnected';

export interface SyncStatus {
  // Connection state
  isOnline: boolean;
  isConnected: boolean;
    
  // Real-time transport connection (SSE or WebSocket)
  realtimeConnected: boolean; // Replaces sseConnected for transport-agnostic naming
  realtimeTransport: SyncTransport; // Which transport is being used
  connectionState: ConnectionState; // Detailed connection state
  lastPingAt?: string;
  reconnectAttempts?: number; // Number of reconnection attempts
  
  // Sync activity
  isSyncing: boolean;
  lastSyncAt?: string;
  lastSuccessfulSyncAt?: string;
  
  // Pull sync state
  isPulling: boolean;
  pullProgress?: {
    collection: string;
    current: number;
    total: number;
  };
  
  // Push sync state
  isPushing: boolean;
  pendingPushCount: number;
  failedPushCount: number;
  
  // Conflicts
  unresolvedConflictCount: number;
  
  // Errors
  lastError?: SyncErrorState;
  
  // Health indicators
  healthStatus: 'healthy' | 'degraded' | 'offline' | 'error' | 'idle' | 'undetermined';
  syncQuality: 'excellent' | 'good' | 'poor' | 'critical';

  // Measured link quality from the connection-quality monitor (rolling RTT
  // median + browser connection hints + failure streaks). 'offline' mirrors
  // the browser offline state; server reachability is still isConnected.
  linkQuality: LinkQuality;
  linkDetail?: {
    medianRttMs: number | null;
    effectiveType: string | null;
    downlinkMbps: number | null;
    saveData: boolean;
  };
    
  // Backward compatibility (deprecated)
  /** @deprecated Use realtimeConnected instead */
  sseConnected: boolean;
}

type SyncStatusListener = (status: SyncStatus) => void;

// ---------------------------------------------------------------------------
// Per-collection push state.
//
// The store tracks pending/failed/pushing per collection because
// `syncCollection()` in replicationFactory.ts runs N collections concurrently.
// The global `pendingPushCount`, `failedPushCount`, and `isPushing` are
// derived by summing/oring across all entries — that way one collection
// finishing cannot zero out another collection's state.
// ---------------------------------------------------------------------------
interface CollectionPushState {
  pending: number;
  failed: number;
  pushing: boolean;
}

class SyncStatusStore {
  private collectionPushState = new Map<string, CollectionPushState>();

  private status: SyncStatus = {
    isOnline: navigator.onLine,
    isConnected: false,
    sseConnected: false, // Deprecated, kept for backward compatibility
    realtimeConnected: false,
    realtimeTransport: 'unknown',
    connectionState: 'disconnected',
    isSyncing: false,
    isPulling: false,
    isPushing: false,
    pendingPushCount: 0,
    failedPushCount: 0,
    unresolvedConflictCount: 0,
    healthStatus: 'offline',
    syncQuality: 'critical',
    linkQuality: 'excellent',
  };

  private listeners = new Set<SyncStatusListener>();

  constructor() {
    // Listen to browser online/offline events
    window.addEventListener('online', () => this.setOnline(true));
    window.addEventListener('offline', () => this.setOnline(false));
    // Fold measured link quality into health — poor links surface as
    // 'degraded' even while the socket is up.
    subscribeLinkQuality((snapshot) => {
      this.updateStatus({
        linkQuality: snapshot.quality,
        linkDetail: {
          medianRttMs: snapshot.medianRttMs,
          effectiveType: snapshot.effectiveType,
          downlinkMbps: snapshot.downlinkMbps,
          saveData: snapshot.saveData,
        },
      });
      this.recalculateHealth();
    });
  }

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------
  
  getStatus(): SyncStatus {
    return { ...this.status };
  }

  subscribe(listener: SyncStatusListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // ---------------------------------------------------------------------------
  // Setters — Connection state
  // ---------------------------------------------------------------------------
  
  setOnline(online: boolean): void {
    this.updateStatus({ isOnline: online });
    this.recalculateHealth();
  }

  setConnected(connected: boolean): void {
    this.updateStatus({ isConnected: connected });
    this.recalculateHealth();
  }

  updateLastPing(): void {
    this.updateStatus({ lastPingAt: new Date().toISOString() });
  }

  /**
   * Set real-time connection status (WebSocket)
   */
  setRealtimeConnected(
    connected: boolean, 
    transport: SyncTransport = 'websocket',
    connectionState?: ConnectionState,
    reconnectAttempts?: number
  ): void {
    this.updateStatus({ 
      realtimeConnected: connected,
      sseConnected: connected, // Keep deprecated field in sync for backward compatibility
      realtimeTransport: transport,
      connectionState: connectionState ?? (connected ? 'connected' : 'disconnected'),
      reconnectAttempts,
      lastPingAt: connected ? new Date().toISOString() : this.status.lastPingAt,
    });
    this.recalculateHealth();
  }

  /**
   * Update connection state for reconnection scenarios
   */
  setConnectionState(state: ConnectionState, reconnectAttempts?: number): void {
    this.updateStatus({
      connectionState: state,
      reconnectAttempts,
      realtimeConnected: state === 'connected',
      sseConnected: state === 'connected', // Keep deprecated field in sync
    });
    this.recalculateHealth();
  }

  // ---------------------------------------------------------------------------
  // Setters — Sync activity
  // ---------------------------------------------------------------------------
  
  setSyncing(syncing: boolean): void {
    this.updateStatus({ isSyncing: syncing });
    this.recalculateHealth();
  }

  setSyncSuccess(): void {
    const now = new Date().toISOString();
    this.updateStatus({
      lastSyncAt: now,
      lastSuccessfulSyncAt: now,
      isSyncing: false,
    });
    this.recalculateHealth();
  }

  // ---------------------------------------------------------------------------
  // Setters — Pull sync
  // ---------------------------------------------------------------------------
  
  setPulling(pulling: boolean, collection?: string, current?: number, total?: number): void {
    this.updateStatus({
      isPulling: pulling,
      pullProgress: pulling && collection ? { collection, current: current ?? 0, total: total ?? 0 } : undefined,
    });
  }

  // ---------------------------------------------------------------------------
  // Setters — Push sync
  // ---------------------------------------------------------------------------
  
  /**
   * @deprecated Use `setCollectionPushState(collection, { pushing })` so
   *   parallel collections don't overwrite each other's flag. This setter
   *   is kept only as a global override (e.g. on full reset) and forces
   *   `isPushing` independent of the per-collection map.
   */
  setPushing(pushing: boolean): void {
    this.updateStatus({ isPushing: pushing });
  }

  /**
   * @deprecated Use `setCollectionPushState(collection, { pending })`.
   *   Direct global writes will be overwritten by the next aggregator pass.
   */
  setPendingPushCount(count: number): void {
    this.updateStatus({ pendingPushCount: count });
    this.recalculateHealth();
  }

  /**
   * @deprecated Use `setCollectionPushState(collection, { failed })`.
   *   Direct global writes will be overwritten by the next aggregator pass.
   */
  setFailedPushCount(count: number): void {
    this.updateStatus({ failedPushCount: count });
    this.recalculateHealth();
  }

  /**
   * Aggregator-friendly setter for per-collection push state. Each call
   * updates only the keys provided for the named collection, then
   * recomputes the global counters by summing across every collection.
   *
   * - `pending`: number of docs still queued for push for this collection.
   * - `failed`:  count of consecutive failed push attempts for this collection.
   * - `pushing`: whether this collection is currently mid-push.
   *
   * Global state is derived (not overwritten):
   *   pendingPushCount = Σ pending
   *   failedPushCount  = Σ failed
   *   isPushing        = any(pushing)
   */
  setCollectionPushState(collection: string, partial: Partial<CollectionPushState>): void {
    const cur = this.collectionPushState.get(collection) ?? { pending: 0, failed: 0, pushing: false };
    const next: CollectionPushState = {
      pending: partial.pending !== undefined ? partial.pending : cur.pending,
      failed: partial.failed !== undefined ? partial.failed : cur.failed,
      pushing: partial.pushing !== undefined ? partial.pushing : cur.pushing,
    };
    // GC: drop entries that are fully zeroed so the map doesn't grow unbounded
    // across every collection name we ever pushed during the session.
    if (next.pending === 0 && next.failed === 0 && !next.pushing) {
      this.collectionPushState.delete(collection);
    } else {
      this.collectionPushState.set(collection, next);
    }
    this.recomputePushAggregates();
  }

  /**
   * Clear per-collection push state for a single collection. Used by
   * replicationFactory on graceful disconnect/cancel so a stale collection
   * doesn't keep `isPushing` true forever.
   */
  clearCollectionPushState(collection: string): void {
    if (this.collectionPushState.delete(collection)) {
      this.recomputePushAggregates();
    }
  }

  /**
   * Whether push is fully drained, optionally scoped to a specific set of
   * collections rather than the global aggregate.
   *
   * Scoping matters for short-lived, self-contained replication sessions
   * (e.g. the mobile pre-wipe "flush pending writes" session in
   * `DBProvider.tsx`) that must not be affected by — or report a false
   * "drained" for — push activity from a completely unrelated collection
   * that happens to be mid-push elsewhere at the same moment. When
   * `collections` is omitted, behaves exactly like the global aggregate
   * (`pendingPushCount === 0 && !isPushing`) for backward compatibility.
   */
  isDrainedFor(collections?: readonly string[]): boolean {
    if (!collections) {
      return this.status.pendingPushCount === 0 && !this.status.isPushing;
    }
    for (const collection of collections) {
      const state = this.collectionPushState.get(collection);
      if (state && (state.pending > 0 || state.pushing)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Sum of `pending` docs across a specific set of collections (or every
   * collection when omitted). Used to report an actual remaining count —
   * rather than a boolean — when a bounded flush (e.g. the mobile pre-wipe
   * safeguard) times out without fully draining, so the caller can decide
   * whether there is real data at risk worth warning the user about.
   *
   * A collection with `pushing: true` counts as AT LEAST 1, even if its own
   * `pending` counter currently reads 0. `syncCollection()` dequeues a
   * batch from `pending` at the moment it starts pushing that batch (before
   * the server has acknowledged it), so a collection can be genuinely
   * mid-push — with a real, unacknowledged batch in flight — while
   * `pending` momentarily reads 0. Without this guard, `isDrainedFor()`
   * (which also checks `pushing`) can correctly report "not drained" for
   * such a collection while this function reported "0 pending" for the
   * exact same state — the caller would then see `{ flushed: false,
   * pendingCount: 0 }` and conclude there was nothing at risk worth warning
   * about, silently discarding an in-flight unacknowledged batch.
   */
  getPendingCountFor(collections?: readonly string[]): number {
    const keys = collections ?? Array.from(this.collectionPushState.keys());
    let total = 0;
    for (const collection of keys) {
      const state = this.collectionPushState.get(collection);
      if (!state) continue;
      total += state.pushing ? Math.max(state.pending, 1) : state.pending;
    }
    return total;
  }

  private recomputePushAggregates(): void {
    let pending = 0;
    let failed = 0;
    let pushing = false;
    for (const s of this.collectionPushState.values()) {
      pending += s.pending;
      failed += s.failed;
      if (s.pushing) pushing = true;
    }
    this.updateStatus({
      pendingPushCount: pending,
      failedPushCount: failed,
      isPushing: pushing,
    });
    this.recalculateHealth();
  }

  // ---------------------------------------------------------------------------
  // Setters — Conflicts
  // ---------------------------------------------------------------------------
  
  setUnresolvedConflictCount(count: number): void {
    this.updateStatus({ unresolvedConflictCount: count });
    this.recalculateHealth();
  }

  // ---------------------------------------------------------------------------
  // Setters — Errors
  // ---------------------------------------------------------------------------
  
  setError(message: string, type: SyncErrorType = 'unknown'): void {
    this.updateStatus({
      lastError: {
        message,
        timestamp: new Date().toISOString(),
        type,
      },
      healthStatus: 'error',
    });
  }

  clearError(): void {
    this.updateStatus({ lastError: undefined });
    this.recalculateHealth();
  }

  // ---------------------------------------------------------------------------
  // Health calculation
  // ---------------------------------------------------------------------------
  
  private recalculateHealth(): void {
    let healthStatus: SyncStatus['healthStatus'] = 'healthy';
    let syncQuality: SyncStatus['syncQuality'] = 'excellent';

    // Offline = offline
    if (!this.status.isOnline || this.status.linkQuality === 'offline') {
      healthStatus = 'offline';
      syncQuality = 'critical';
    }
    // Not connected = offline
    else if (!this.status.isConnected) {
      healthStatus = 'offline';
      syncQuality = 'critical';
    }
    // Has errors = error
    else if (this.status.lastError) {
      healthStatus = 'error';
      syncQuality = 'critical';
    }
    // Has failed pushes or conflicts = degraded (work it cannot complete)
    else if (this.status.failedPushCount > 0 || this.status.unresolvedConflictCount > 0) {
      healthStatus = 'degraded';
      syncQuality = this.status.failedPushCount > 10 ? 'poor' : 'good';
    }
    // Measured poor link = degraded (connected but degraded service)
    else if (this.status.linkQuality === 'poor') {
      healthStatus = 'degraded';
      syncQuality = 'poor';
    }
    // Has pending pushes or is actively syncing = healthy (work in flight)
    else if (this.status.pendingPushCount > 0 || this.status.isSyncing || this.status.isPushing) {
      healthStatus = 'healthy';
      syncQuality = this.status.pendingPushCount > 20 || this.status.linkQuality === 'good'
        ? 'good'
        : 'excellent';
    }
    // Connected with nothing to sync = idle (not "healthy" — nothing to judge)
    else {
      healthStatus = 'idle';
      syncQuality = this.status.linkQuality === 'good' ? 'good' : 'excellent';
    }

    this.updateStatus({ healthStatus, syncQuality });
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------
  
  private updateStatus(partial: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...partial };
    this.notifyListeners();
  }

  private notifyListeners(): void {
    const status = this.getStatus();
    this.listeners.forEach(listener => listener(status));
  }

  // ---------------------------------------------------------------------------
  // Reset
  // ---------------------------------------------------------------------------
  
  reset(): void {
    this.collectionPushState.clear();
    this.status = {
      isOnline: navigator.onLine,
      isConnected: false,
      sseConnected: false, // Deprecated, kept for backward compatibility
      realtimeConnected: false,
      realtimeTransport: 'unknown',
      connectionState: 'disconnected',
      isSyncing: false,
      isPulling: false,
      isPushing: false,
      pendingPushCount: 0,
      failedPushCount: 0,
      unresolvedConflictCount: 0,
      healthStatus: 'offline',
      syncQuality: 'critical',
      linkQuality: 'excellent',
    };
    this.notifyListeners();
  }
}

// ---------------------------------------------------------------------------
// Singleton instance
// ---------------------------------------------------------------------------

export const syncStatusStore = new SyncStatusStore();

// ---------------------------------------------------------------------------
// Convenience exports
// ---------------------------------------------------------------------------

export const getSyncStatus = () => syncStatusStore.getStatus();
export const subscribeSyncStatus = (listener: SyncStatusListener) => syncStatusStore.subscribe(listener);

export const setOnline = (online: boolean) => syncStatusStore.setOnline(online);
export const setConnected = (connected: boolean) => syncStatusStore.setConnected(connected);

// WebSocket real-time connection
export const setRealtimeConnected = (
  connected: boolean,
  transport?: SyncTransport,
  connectionState?: ConnectionState,
  reconnectAttempts?: number
) => syncStatusStore.setRealtimeConnected(connected, transport, connectionState, reconnectAttempts);

export const setConnectionState = (state: ConnectionState, reconnectAttempts?: number) =>
  syncStatusStore.setConnectionState(state, reconnectAttempts);

export const updateLastPing = () => syncStatusStore.updateLastPing();

export const setSyncing = (syncing: boolean) => syncStatusStore.setSyncing(syncing);
export const setSyncSuccess = () => syncStatusStore.setSyncSuccess();

export const setPulling = (pulling: boolean, collection?: string, current?: number, total?: number) => 
  syncStatusStore.setPulling(pulling, collection, current, total);

export const setPushing = (pushing: boolean) => syncStatusStore.setPushing(pushing);
export const setPendingPushCount = (count: number) => syncStatusStore.setPendingPushCount(count);
export const setFailedPushCount = (count: number) => syncStatusStore.setFailedPushCount(count);
export const setCollectionPushState = (
  collection: string,
  partial: { pending?: number; failed?: number; pushing?: boolean },
) => syncStatusStore.setCollectionPushState(collection, partial);
export const clearCollectionPushState = (collection: string) =>
  syncStatusStore.clearCollectionPushState(collection);
export const getPendingPushCountFor = (collections?: readonly string[]) =>
  syncStatusStore.getPendingCountFor(collections);

export const setUnresolvedConflictCount = (count: number) => syncStatusStore.setUnresolvedConflictCount(count);

export const setSyncError = (message: string, type?: SyncErrorType) => 
  syncStatusStore.setError(message, type);
export const clearSyncError = () => syncStatusStore.clearError();

export const resetSyncStatus = () => syncStatusStore.reset();

/**
 * @process waitForPendingPushDrain
 *
 * Steps:
 * 1. If there are no pending/pushing writes right now, resolve immediately.
 * 2. Otherwise subscribe to the sync status store and resolve as soon as
 *    `pendingPushCount === 0 && !isPushing` is observed.
 * 3. Race against `timeoutMs` so a stalled or offline push queue cannot hang
 *    the caller forever — resolves (does not reject) on timeout so callers
 *    can proceed with recovery regardless of outcome.
 *
 * Inputs:
 * - timeoutMs: Maximum time to wait for the push queue to drain (default 15000ms).
 * - collections: Optional — when provided, only these Dexie collection names
 *   are checked for drain (via `syncStatusStore.isDrainedFor`), so an
 *   unrelated collection's in-flight push elsewhere cannot delay or corrupt
 *   this specific wait. When omitted, checks the global aggregate across
 *   every collection (original/default behavior, unchanged).
 *
 * Outputs:
 * - Promise<boolean>: true if the queue drained before the timeout, false if
 *   the timeout elapsed first (including while offline, where the queue can
 *   never drain).
 *
 * Failure Handling:
 * - Never throws or rejects — used as a best-effort safeguard before a
 *   destructive local-db recovery action (e.g. wipe-and-rebuild), so it must
 *   never block that recovery from eventually proceeding.
 *
 * Idempotency:
 * - REQUIRED — safe to call multiple times; each call is independent.
 */
export function waitForPendingPushDrain(
  timeoutMs = 15_000,
  collections?: readonly string[],
): Promise<boolean> {
  const isDrained = () => syncStatusStore.isDrainedFor(collections);

  if (isDrained()) {
    return Promise.resolve(true);
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (drained: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(drained);
    };

    const unsubscribe = syncStatusStore.subscribe(() => {
      if (isDrained()) finish(true);
    });

    const timer = setTimeout(() => finish(false), timeoutMs);
  });
}
